/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Field, ErrorMessage } from "formik";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Toggle from "react-toggle";

/* Helpers, constants, custom methods, internal libraries */
import { POLICY_ROUTE } from "../../../constants/routes.js";
import { useHasPermission } from "../../../hooks/useHasPermission.js";
import { getBasicEconomyLink, getCurrencySymbol, getCurrencyLocale } from '../../../helpers/general.js';
import { useUserContext } from "../../../contexts/userContext.js";
import { usePolicyService } from "services/policyService.js";

/* Components */
import UCButton from "../../../components/buttons/index.js";
import UCToggle from "../../../components/uctoggle/index.js";
import UCSelect from "../../../components/ucselect/index.js";
import { ReactComponent as InfoIconSvg } from "../../../resources/images/info-icon.svg";
import { ReactComponent as DoubleChevronRightIcon } from "../../../resources/images/chevron-double-right.svg";

/* Styles */
import styles, { selectField, currencyDropdownStyles } from "../styles.js";
import generalStyles from "../../../resources/styles/general.js";
import { colors } from "../../../constants/colors.js";

const PolicySettingsCard = ({
  formRef,
  values,
  handleFormSubmit,
  setFieldValue,
  showSaveBtn,
  policyResponse,
}) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const general = generalStyles({ theme });
  const selectFieldStyles = selectField(theme);
  const navigate = useNavigate();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode
  const isChoice = providerCode === "CHOICE";
  const { getBrandOptions, getCurrencyOptions } = usePolicyService();
  const [showTooltip, setShowTooltip] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const optionBrands = await getBrandOptions('O_CHOICE_BRANDS');
      setBrandOptions(optionBrands?.options[0]?.options || []);
    })();
    (async () => {
      const optionCurrencies = await getCurrencyOptions('O_COMPANYSETTINGS_CURRENCY');
      const opts = (optionCurrencies?.options[0]?.options || []).map(opt => ({
        value: opt.optionValue,
        label: opt.optionText
      }));
      setCurrencyOptions(opts);
    })();
  }, []);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm();
    }
    navigate("../" + POLICY_ROUTE);
  };

  const allowNonRefundableOptions = [
    {
      value: "true",
      label: <FormattedMessage id="editHotelPolicy.allowRefundableOptions.allowed" />,
    },
    {
      value: "false",
      label: <FormattedMessage id="editHotelPolicy.allowRefundableOptions.notAllowed" />
    },
  ];

  const advancePurchaseOptions = Array.from({ length: 60 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (
      <FormattedMessage
        id={
          i === 0
            ? "editPolicy.advancePurchaseOptions.day"
            : "editPolicy.advancePurchaseOptions.days"
        }
        values={{ count: i + 1 }}
      />
    ),
  }));


  const starRatingOptions = [
    { value: "1", label: <FormattedMessage id="editHotelPolicy.starRatingOptions.1star" /> },
    { value: "2", label: <FormattedMessage id="editHotelPolicy.starRatingOptions.2star" /> },
    { value: "3", label: <FormattedMessage id="editHotelPolicy.starRatingOptions.3star" /> },
    { value: "4", label: <FormattedMessage id="editHotelPolicy.starRatingOptions.4star" /> },
    { value: "5", label: <FormattedMessage id="editHotelPolicy.starRatingOptions.5star" /> },
  ];

  return (
    <div className={classes.policyContainer}>
      <div className={classes.policySettingsHeader}>
        <div>
          <h3>
            <FormattedMessage id="editHotelPolicy.settingsTitle" />
          </h3>
          <p>
            <FormattedMessage id="editHotelPolicy.settingsSubtitle" />
          </p>
        </div>
        {checkPermission("F_POLICY_MANAGEPOLICY") && (
          <div className={classes.btnContainer}>
            <UCButton
              type="default"
              className={classes.cancelBtn}
              onClick={cancelUpdate}
              text={<FormattedMessage id="editPolicy.cancelBtn" />}
            />
            <UCButton
              type="primary"
              onClick={handleFormSubmit}
              text={<FormattedMessage id="editPolicy.saveBtn" />}
              disabled={!formRef.current?.dirty && !showSaveBtn}
              spinner={{
                name: "editPolicySpinner",
                size: 20,
                color: colors.white,
              }}
            />
          </div>
        )}
      </div>
      <div className={classes.policyCard}>
        {isChoice && (
          <>
            <div className={classes.policyCardRow}>
              <div className={classes.policyRowName}>
                <h6>
                  <FormattedMessage id="editHotelPolicy.settingsMaxPriceTitle"/>
                </h6>
                <p>
                  <FormattedMessage id="editHotelPolicy.settingsMaxPriceSubtitle"/>
                </p>
              </div>
              <div className={classes.policyRowAction}>
                <div className={classes.priceInputWrapper}>
                  <Field
                    type="number"
                    name="maxPricePerNight"
                    id="maxPricePerNight"
                  >
                    {({ field, form }) => (
                      <div>
                        <div className={classes.priceInputUnified}>
                          <div className={classes.priceInputContent}>
                            <span className={classes.priceInputDollar}>{getCurrencySymbol(values.currency || 'USD')}</span>
                            <input
                              {...field}
                              type="text"
                              placeholder="0"
                              className={classes.priceInputField}
                              value={
                                field.value !== '' && field.value !== undefined && field.value !== null
                                  ? new Intl.NumberFormat(getCurrencyLocale(values.currency || 'USD'), { maximumFractionDigits: 0 }).format(Number(field.value))
                                  : ''
                              }
                              onChange={(e) => {
                                const raw = e.target.value.replace(/,/g, '');
                                if (raw === "" || (/^\d+$/.test(raw) && raw.length <= 5)) {
                                  form.setFieldValue("maxPricePerNight", raw);
                                }
                              }}
                            />
                          </div>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip className={`${general.main} ${classes.tooltip}`}>
                                <div className={classes.tooltipDescription}>
                                  <FormattedMessage id="editHotelPolicy.settingsMaxPriceSubtitle" />
                                </div>
                              </Tooltip>
                            }
                          >
                            <InfoIconSvg className={classes.priceInputIcon} />
                          </OverlayTrigger>
                          <div className={classes.priceInputDropdownSection}>
                            <Field name="currency">
                              {({ field: currencyField }) => (
                                <UCSelect
                                  key={currencyOptions.length}
                                  {...currencyField}
                                  styles={currencyDropdownStyles(selectFieldStyles, theme) }
                                  options={currencyOptions}
                                  value={values.currency || "USD"}
                                  onChange={(item) => form.setFieldValue("currency", item.value)}
                                  isSearchable={false}
                                />
                              )}
                            </Field>
                          </div>
                        </div>
                        <div className={classes.errorWrapper}>
                          <ErrorMessage name="maxPricePerNight" component="span" />
                        </div>
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            </div>
            <hr className={classes.divider} />
          </>
        )}
        {!isChoice && (
          <>
            <div className={classes.policyCardRow}>
              <div className={classes.policyRowName}>
                <h6>
                  <FormattedMessage id="editPolicy.settingsBasicTitle" />
                </h6>
                <p>
                  <FormattedMessage id="editPolicy.settingsBasicSubtitle" />
                </p>
              </div>
              <div className={classes.policyRowAction}>
                <div className={classes.toggle}>
                  <Toggle
                    icons={false}
                    onChange={(e) => {
                      setFieldValue("allowBasicFares", e.target.checked);
                    }}
                    onClick={e => e.currentTarget.blur()}
                    checked={values.allowBasicFares}
                    disabled={values.requiredRefundableFares}
                  />
                  <div className={classes.toggleLabel}>
                    <span>
                      <FormattedMessage id="editPolicy.settingsAllowToggle" />
                    </span>
                    <div>
                      <OverlayTrigger
                        placement="top"
                        show={showTooltip}
                        overlay={
                          <Tooltip
                            className={`${general.main} ${classes.tooltip}`}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          >
                            <div className={classes.tooltipTitle}>
                              <FormattedMessage id="editPolicy.allowTooltipTitle" />
                            </div>
                            <div className={classes.tooltipDescription}>
                              <FormattedMessage id="editPolicy.allowTooltipSubtitle" />
                              <Link
                                to={getBasicEconomyLink(providerCode)}
                                target="_blank"
                              >
                                <FormattedMessage id="editPolicy.allowTooltipLink" />{" "}
                                <DoubleChevronRightIcon />
                              </Link>
                            </div>
                          </Tooltip>
                        }
                      >
                        <InfoIconSvg
                          className={classes.infoIcon}
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        />
                      </OverlayTrigger>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <hr className={classes.divider} />
          </>
        )}
        {!isChoice && (
          <>
            <div className={classes.policyCardRow}>
              <div className={classes.policyRowName}>
                <h6>
                  <FormattedMessage id="editPolicy.settingsAdvancedTitle" />
                </h6>
                <p>
                  <FormattedMessage id="editPolicy.settingsAdvancedSubtitle" />
                </p>
              </div>
              <div className={classes.policyRowAction}>
                <div>
                  <UCToggle
                    label={
                      <div className={classes.toggleLabel}>
                        <span>
                          <FormattedMessage id="editPolicy.settingsRequireToggle" />
                        </span>
                        <div>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip
                                className={`${general.main} ${classes.tooltip}`}
                              >
                                <div className={classes.tooltipTitle}>
                                  <FormattedMessage id="editPolicy.requireTooltipTitle" />
                                </div>
                                <div className={classes.tooltipDescription}>
                                  <FormattedMessage id="editPolicy.requireTooltipSubtitle" />
                                </div>
                              </Tooltip>
                            }
                          >
                            <InfoIconSvg className={classes.infoIcon} />
                          </OverlayTrigger>
                        </div>
                      </div>
                    }
                    icons={null}
                    onChange={(e) => {
                      setFieldValue("requiredAdvancePurchase", e.target.checked);
                      setFieldValue(
                        "advancePurchase",
                        e.target.checked ? "1" : null
                      );
                    }}
                    defaultChecked={values.requiredAdvancePurchase}
                  />
                  <InfoIconSvg className={classes.advancePurchaseInfoIcon} />
                  {values.requiredAdvancePurchase && (
                    <Field
                      type="select"
                      name="advancePurchase"
                      id="advancePurchase"
                    >
                      {({ form }) => (
                        <div>
                          <UCSelect
                            className={classes.selectAdvancedField}
                            options={advancePurchaseOptions}
                            value={values.advancePurchase}
                            defaultValue={advancePurchaseOptions.find(
                              (item) => item.value === values.advancePurchase
                            )}
                            onChange={(item) =>
                              form.setFieldValue("advancePurchase", item.value)
                            }
                          />
                        </div>
                      )}
                    </Field>
                  )}
                </div>
              </div>
            </div>
            <hr className={classes.divider} />
          </>
        )}
        {isChoice && policyResponse?.showAdvancePurchase && (
          <>
            <div className={classes.policyCardRow}>
              <div className={classes.policyRowName}>
                <h6>
                  <FormattedMessage id="editHotelPolicy.settingsAdvancePurchaseTitle" />
                </h6>
                <p>
                  <FormattedMessage id="editHotelPolicy.settingsAdvancePurchaseSubtitle" />
                </p>
              </div>
              <div className={classes.policyRowAction}>
                <div>
                  <UCToggle
                    label={
                      <div className={classes.toggleLabel}>
                        <span>
                          <FormattedMessage id="editHotelPolicy.settingsAdvancePurchaseToggle" />
                        </span>
                      </div>
                    }
                    icons={false}
                    onChange={(e) => {
                      setFieldValue("requiredAdvancePurchase", e.target.checked);
                      setFieldValue(
                        "advancePurchase",
                        e.target.checked ? "1" : null
                      );
                    }}
                    defaultChecked={values.requiredAdvancePurchase}
                  />
                  {values.requiredAdvancePurchase && (
                    <Field
                      type="select"
                      name="advancePurchase"
                      id="advancePurchase"
                    >
                      {({ form }) => (
                        <div>
                          <UCSelect
                            className={classes.selectAdvancedField}
                            options={advancePurchaseOptions}
                            value={values.advancePurchase}
                            defaultValue={advancePurchaseOptions.find(
                              (item) => item.value === values.advancePurchase
                            )}
                            onChange={(item) =>
                              form.setFieldValue("advancePurchase", item.value)
                            }
                          />
                        </div>
                      )}
                    </Field>
                  )}
                </div>
              </div>
            </div>
            <hr className={classes.divider} />
          </>
        )}
        <div className={classes.policyCardRow}>
          <div className={classes.policyRowName}>
            <h6>
              <FormattedMessage id="editHotelPolicy.settingsRefundableRatesTitle" />
            </h6>
            <p>
              <FormattedMessage id="editHotelPolicy.settingsRefundableRatesSubtitle" />
            </p>
          </div>
          <div className={classes.policyRowAction}>
            <div className={classes.policySelectWrapper}>
              <Field
                type="select"
                name="allowNonRefundableFares"
                id="allowNonRefundableFares"
              >
                {({ form }) => (
                  <div>
                    <UCSelect
                      options={allowNonRefundableOptions}
                      value={String(values.allowNonRefundableFares)}
                      defaultValue={allowNonRefundableOptions.find(
                        (item) =>
                          item.value === String(values.allowNonRefundableFares)
                      )}
                      onChange={(item) => {
                        form.setFieldValue(
                          "allowNonRefundableFares",
                          item.value === "true"
                        );
                        item.value !== "true" &&
                          form.setFieldValue("requiredRefundableFares", false);
                      }}
                    />
                  </div>
                )}
              </Field>
            </div>
          </div>
        </div>
      </div>
      {isChoice && (
        <div className={classes.policyContainer}>
          <div className={classes.policySettingsHeader}>
            <div>
              <h3>
                <FormattedMessage id="editHotelPolicy.travelExperienceTitle" />
              </h3>
              <p>
                <FormattedMessage id="editHotelPolicy.travelExperienceSubtitle" />
              </p>
            </div>
          </div>
          <div className={classes.policyCard}>
            {policyResponse?.showMinimumRating && (
              <>
                <div className={classes.policyCardRow}>
                  <div className={classes.policyRowName}>
                    <h6>
                      <FormattedMessage id="editHotelPolicy.starRatingTitle" />
                    </h6>
                    <p>
                      <FormattedMessage id="editHotelPolicy.starRatingSubtitle" />
                    </p>
                  </div>
                  <div className={classes.policyRowAction}>
                    <div className={classes.selectFieldWrapper}>
                      <Field
                        type="select"
                        name="minimumStarRating"
                        id="minimumStarRating"
                      >
                        {({ form }) => (
                          <div>
                            <UCSelect
                              options={starRatingOptions}
                              value={values.minimumStarRating || "3"}
                              defaultValue={starRatingOptions.find(
                                (item) => item.value === (values.minimumStarRating || "3")
                              )}
                              onChange={(item) =>
                                form.setFieldValue("minimumStarRating", item.value)
                              }
                            />
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>
                </div>
                <hr className={classes.divider} />
              </>
            )}
            <div className={classes.policyCardRow}>
              <div className={classes.policyRowName}>
                <h6>
                  <FormattedMessage id="editHotelPolicy.settingsBrandsTitle" />
                </h6>
                <p>
                  <FormattedMessage id="editHotelPolicy.settingsBrandsSubtitle" />
                </p>
              </div>
              <div className={classes.policyRowAction}>
                <div className={classes.selectFieldWrapper}>
                  <Field
                    type="select"
                    name="allowableBrands"
                    id="allowableBrands"
                  >
                    {({ form }) => {
                      return (
                        <div>
                          <UCSelect
                            isMulti={true}
                            className={classes.roles}
                            displayName={<FormattedMessage id="editHotelPolicy.settingsBrandsTitle" />}
                            options={brandOptions}
                            defaultValue={brandOptions.filter(option => values.allowableBrands?.some(def => (typeof def === 'string' ? def : def.optionValue) === option.optionValue))}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                            onChange={(item) => {
                              form.setFieldValue("allowableBrands", item)
                            }}
                            getOptionLabel={(option) => {
                              return (
                                <div className={classes.brandLabel}>
                                  <div className={classes.brandName}>{option.optionText}</div>
                                </div>
                              )
                            }}
                            getOptionValue={(option) => option.optionValue}
                          />
                        </div>
                      );
                    }}
                  </Field>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicySettingsCard;